import { actions, IInstrumentStore, InstrumentStore, INSTRUMENT_STORE_NAME, IStore, TiprankStatus } from './store';
import { frontendActions, FrontendLocations, getConfig } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, webTradingApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { IDividendsResponse, InstrumentClass, InstrumentCode } from './types';

export const INSTRUMENT_API_NAMES = {
  ProductDetailsGet3: `ProductDetailsGet3`,
  StockLogoGet1: `StockLogoGet1`,
  ProductLibrarySearchCodesNames4: `ProductLibrarySearchCodesNames4`,
  UpcomingDividendsGet1: `UpcomingDividendsGet1`,
  NewsIndicatorBatchStockGet1: `NewsIndicatorBatchStockGet1`,
  ResearchESGDataGet1: 'ResearchESGDataGet1',
  ProductLibrarySearch2: 'ProductLibrarySearch2'
};

export type GetState = typeof InstrumentStore.getState;

export const getUnfetchedInstruments = (instruments: InstrumentCode | InstrumentCode[], key: keyof IInstrumentStore, getState: GetState) => {
  const array = Array.isArray(instruments) ? instruments : [instruments];
  const existing = Object.keys(getState()[INSTRUMENT_STORE_NAME][key]);
  return array.filter((instrument) => !existing.includes(instrument));
};

export const getUnfetchedFullInstrumentsDetails = (instruments: InstrumentCode | InstrumentCode[], getState: GetState) => {
  const array = Array.isArray(instruments) ? instruments : [instruments];
  const currentDetails = getState()[INSTRUMENT_STORE_NAME].details;

  return array.filter((instrument) => !currentDetails[instrument]?.fullDetail);
};

/**
 * Pass in single or array of instrument codes to fetch, only those that haven't been fetched will be requested.
 * @param instruments
 * @param fullDetails - request details if `fullDetail` is not yet fetched
 * @returns
 */
export const fetchInstrumentDetails =
  (instruments: InstrumentCode | InstrumentCode[], fullDetails?: boolean) => async (dispatch: Dispatch<any>, getState: GetState) => {
    const toFetch = fullDetails ? getUnfetchedFullInstrumentsDetails(instruments, getState) : getUnfetchedInstruments(instruments, 'details', getState);
    const request = { AsxCodes: toFetch.join(',') };

    try {
      if (toFetch.length > 0) {
        const response = await frontendActions.webTradingApiCall<CmcWebApi.MarketInformation.ProductsStaticDataModel2>(
          INSTRUMENT_STORE_NAME,
          INSTRUMENT_API_NAMES.ProductDetailsGet3,
          request,
          dispatch
        );
        dispatch(actions.detailsFetched(response.Products));
      }
    } catch {}
  };

/**
 * Pass in single or array of instrument codes to fetch, only those that haven't been fetched will be requested.
 * Both logo and details will be fetched
 * @param instruments
 * @param fullDetails - request details if `fullDetail` is not yet fetched
 * @returns
 */
export const fetchInstruments =
  (instruments: InstrumentCode | InstrumentCode[], fullDetails?: boolean, fetchLogo: boolean = true) =>
  async (dispatch: Dispatch<any>, getState: GetState) => {
    const toFetch = getUnfetchedInstruments(instruments, 'logos', getState);
    let toFetchDetails = fullDetails ? getUnfetchedFullInstrumentsDetails(toFetch, getState) : getUnfetchedInstruments(toFetch, 'details', getState);
    if (toFetchDetails.length > 0) await fetchInstrumentDetails(toFetchDetails, fullDetails)(dispatch, getState);

    if (!fetchLogo) return;

    const logoRequests: CmcWebApi.MarketInformation.StockLogoRequest1[] = toFetch
      .filter((instrument) => !getState()[INSTRUMENT_STORE_NAME].logos[instrument])
      .filter((instrument) => getState()[INSTRUMENT_STORE_NAME].details[instrument])
      .map((instrument) => ({ StockCode: instrument, ExchangeId: getState()[INSTRUMENT_STORE_NAME].details[instrument].exchangeId }));

    try {
      const promises = logoRequests.map(async (request) => {
        const response = await frontendActions.webTradingApiCall<{ Model: CmcWebApi.MarketInformation.StockLogoModel1 }>(
          INSTRUMENT_STORE_NAME,
          INSTRUMENT_API_NAMES.StockLogoGet1,
          request,
          dispatch,
          `${request.StockCode}_${request.ExchangeId}`
        );
        dispatch(actions.logoFetched({ instrument: request.StockCode, url: response.Model?.ImageUrl }));
      });
      await Promise.allSettled(promises);
    } catch {}
  };

/**
 * Pass in single or array of instrument codes to fetch, only those that haven't been fetched will be requested.
 * @param instruments
 * @returns
 */
export const fetchDividends = (instruments: InstrumentCode | InstrumentCode[]) => async (dispatch: Dispatch<any>, getState: GetState) => {
  const toFetch = getUnfetchedInstruments(instruments, 'dividends', getState);

  const callWebApi = async () =>
    webTradingApi(getConfig().stockbrokingApiUrl).send<{ Dividends: IDividendsResponse[] }>({
      [INSTRUMENT_API_NAMES.UpcomingDividendsGet1]: {
        StocksFilter: toFetch
      }
    });

  try {
    if (toFetch.length > 0) {
      const response = await frontendActions.apiCall(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.UpcomingDividendsGet1, callWebApi)(dispatch);
      dispatch(
        actions.dividendFetched({
          fetched: toFetch,
          response: response.Dividends
        })
      );
    }
  } catch {}
};

const countryCodeIdMap: Record<string, number> = {
  US: 1,
  CA: 2,
  GB: 3,
  DE: 5,
  AU: 26,
  ES: 19,
  SG: 27,
  HK: 83,
  JP: 73,
  IT: 23,
  FR: 20
};

const callTiprankQueryApi = async (stockCode: string): Promise<TiprankStatus> => {
  const url = getConfig().cloudApiUrl + '/sb/token-service/instrument-query';
  const isoCountryCode = stockCode.split(':').length > 1 ? stockCode.split(':')[1] : 'AU';

  const body = {
    instrumentSearch: {
      ticker: stockCode.split(':')[0],
      countryId: countryCodeIdMap[isoCountryCode]
    }
  };
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    // NOTE: when tiprank support query fails we are returning isSupported as true to still show
    // tiprank button as graceful degradation. Since for unsupported instrument tiprank just show not supported and does not crash.
    // there is higher chance that tiprank support might be there even if query fails.
    if (!response.ok) return { isPending: false, isError: true, isSupported: true };

    const data = await response.json();

    return { isPending: false, isError: false, isSupported: data.isSupported };
  } catch {
    return { isPending: false, isError: true, isSupported: true };
  }
};

export const fetchTiprankSupport = (instrument: InstrumentCode) => async (dispatch: Dispatch<any>, getState: GetState) => {
  const currentDetails = getState()[INSTRUMENT_STORE_NAME].tipRanks[instrument];
  if (currentDetails?.isPending || currentDetails?.isError || currentDetails?.isSupported !== undefined) return;

  dispatch(actions.tipRankSupportPending({ instrument }));
  const response = await callTiprankQueryApi(instrument);
  dispatch(actions.tiprankFetched({ instrument, status: response }));
};

/**
 * Pass in single or array of instrument codes to fetch, only those that haven't been fetched will be requested.
 * @param instruments
 * @returns
 */
export const fetchNews = (instruments: InstrumentCode | InstrumentCode[]) => async (dispatch: Dispatch<any>, getState: GetState) => {
  const toFetch = getUnfetchedInstruments(instruments, 'news', getState);

  const callWebApi = async () =>
    webTradingApi(getConfig().stockbrokingApiUrl).send<CmcWebApi.MarketInformation.NewsIndicatorBatchModel1>({
      [INSTRUMENT_API_NAMES.NewsIndicatorBatchStockGet1]: {
        StockCodes: toFetch.join(',')
      }
    });

  try {
    if (toFetch.length > 0) {
      const response = await frontendActions.apiCall(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.NewsIndicatorBatchStockGet1, callWebApi)(dispatch);
      dispatch(actions.newsFetched(response));
    }
  } catch {}
};

const defaultInstrumentClasses = (Object.keys(InstrumentClass) as InstrumentClass[]).filter(
  (instrumentClass) => ![InstrumentClass.Unknown, InstrumentClass.Combo].includes(instrumentClass)
);
/**
 * Searching products by query and instrumentClasses
 * Do ranking
 * @param location
 * @param query
 * @param maximumResults
 * @param instrumentClasses
 * @returns
 */
export const searchProducts =
  (location: FrontendLocations, query: string, maximumResults: number, instrumentClasses: InstrumentClass[] | undefined = undefined) =>
  async (dispatch: Dispatch<any>, getState: () => IStore) => {
    dispatch(actions.querySearched({ location, query }));

    const instrumentClassesFilter = (instrumentClasses ?? defaultInstrumentClasses).join(',');
    const request: CmcWebApi.MarketInformation.ProductSearchDetailsRequest3 = {
      InstrumentClasses: instrumentClassesFilter,
      MaxResults: maximumResults,
      SearchTerm: query,
      DomesticOnly: false,
      IncludeAlgoEligibility: false
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.MarketInformation.ProductLibrarySearchCodesNames4Model>(
        INSTRUMENT_STORE_NAME,
        INSTRUMENT_API_NAMES.ProductLibrarySearchCodesNames4,
        request,
        dispatch,
        query,
        false,
        false,
        false,
        true
      );

      // check if most results match to most recent query
      if (query !== getState().instrument.searchQuery[location]) return;

      dispatch(actions.productSearchResults({ location, results: response.Results }));
    } catch {}
  };

export const openInstrument = (instrument: InstrumentCode, tradingRoutePrefix: string) => {
  // none of the Falcon pages should cause the target to be anything but Summary page
  var target = location.pathname.startsWith(tradingRoutePrefix) ? location.pathname.slice(tradingRoutePrefix.length) : '';
  return `${tradingRoutePrefix}/stockmanager/intro.aspx?target=${target}&asxcode=${instrument}`;
};

export const openInstrumentNews = (instrument: InstrumentCode, tradingRoutePrefix: string) =>
  `${tradingRoutePrefix}/StockDetails/News/StockNews.aspx?asxcode=${instrument}`;

const INT_MAX = 2147483647;
/**
 * Searching products by countryIsoCode, searchTerm and filters
 * Doesn't do ranking
 * @param location
 * @param countryIsoCode
 * @param searchTerm
 * @param filters
 * @returns
 */
export const searchProductLibrary =
  (
    location: FrontendLocations,
    countryIsoCode: string = '',
    searchTerm: string = '',
    filters: CmcWebApi.MarketInformation.ProductsSearch2PropertyFilter[] = []
  ) =>
  async (dispatch: Dispatch<any>) => {
    const request: CmcWebApi.MarketInformation.ProductSearchRequest2 = {
      CountryIsoCode: countryIsoCode,
      IncludeResults: true,
      PageNumber: 1,
      PageSize: INT_MAX,
      SearchTerm: searchTerm,
      Filters: filters,
      Sorts: [],
      Displays: [],
      GroupCount: ''
    };

    try {
      const response = await frontendActions.webTradingApiCall<CmcWebApi.MarketInformation.ProductsSearchModel2>(
        INSTRUMENT_STORE_NAME,
        INSTRUMENT_API_NAMES.ProductLibrarySearch2,
        request,
        dispatch
      );

      dispatch(actions.productLibrarySearchResult({ location, result: response.Result }));
    } catch {}
  };
